import { collection, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

/** Region / location master document in `locations` collection */
export type RegionLocation = {
    id: string;
    name: string;
    headOfficeAddress?: string;
    /** Same shape as branch `location` geo */
    location?: { lat?: number | null; long?: number | null } | null;
};

export type RegionLocationFormPayload = {
    name: string;
    headOfficeAddress: string;
    latitude: string;
    longitude: string;
};

export async function fetchRegionLocations(): Promise<RegionLocation[]> {
    const snap = await getDocs(collection(db, 'locations'));
    return snap.docs
        .map((x) => {
            const data = x.data();
            return {
                id: x.id,
                name: typeof data.name === 'string' ? data.name : '',
                headOfficeAddress: typeof data.headOfficeAddress === 'string' ? data.headOfficeAddress : '',
                location: data.location as RegionLocation['location'],
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function parseCoordsForLocation(latStr: string, lonStr: string): { lat: number | null; long: number | null } {
    const lat = parseFloat(String(latStr).replace(/,/g, '').trim());
    const long = parseFloat(String(lonStr).replace(/,/g, '').trim());
    return {
        lat: !Number.isNaN(lat) && String(latStr).trim() !== '' ? lat : null,
        long: !Number.isNaN(long) && String(lonStr).trim() !== '' ? long : null,
    };
}

/** Firestore-serializable fields for `locations` writes */
type LocationWritePayload = {
    name: string;
    headOfficeAddress: string;
    location: { lat: number; long: number } | null;
};

function buildLocationFields(form: RegionLocationFormPayload): LocationWritePayload {
    const { lat, long } = parseCoordsForLocation(form.latitude, form.longitude);
    const hasGeo = lat != null && long != null;
    return {
        name: form.name.trim(),
        headOfficeAddress: form.headOfficeAddress.trim(),
        location: hasGeo ? { lat, long } : null,
    };
}

export async function createRegionLocation(form: RegionLocationFormPayload): Promise<string> {
    const ref = await addDoc(collection(db, 'locations'), buildLocationFields(form));
    return ref.id;
}

export async function updateRegionLocation(id: string, form: RegionLocationFormPayload): Promise<void> {
    await updateDoc(doc(db, 'locations', id), buildLocationFields(form));
}
